import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { useGetPersonalInfo } from '@/hooks/api';
import { Close, MenuRight, NosIdWithText } from '@nosinovacao/fe-icons';
import {
  SheetClosePrimitive,
  SheetContentPrimitive,
  SheetPrimitive,
  useI18n,
} from '@nosinovacao/nosid-mfe-common';
import { type ComponentType } from 'react';
import { useNavbarContext, type NavSheetContentSections } from '../../context/navbar';
import { MainNavSheetContent } from './main-section';
import { TvNavSheetContent } from './tv-section';

const navSheetContent: Record<
  NavSheetContentSections,
  ComponentType<{
    onClose: () => void;
    goToSection: (section: Exclude<NavSheetContentSections, 'main'>) => void;
    goToMainSection: () => void;
    currentSection: NavSheetContentSections;
  }>
> = {
  main: MainNavSheetContent,
  tv: TvNavSheetContent,
  services: MainNavSheetContent,
} as const;

export function MobileNavbar() {
  const { t } = useI18n();

  const {
    sheetOpen,
    setSheetOpen,
    activeSection: sectionName,
    setActiveSection: setSectionName,
  } = useNavbarContext();

  const NavSheetContent = navSheetContent[sectionName];

  return (
    <SheetPrimitive open={sheetOpen && sectionName !== 'services'} onOpenChange={setSheetOpen}>
      <SheetContentPrimitive
        skin="light"
        aria-label={t('Common.Navbar.ContentLabel')}
        className="fixed inset-y-0 right-0 z-50 w-dvw bg-nos-neutral-0 py-6 tablet:w-[min(50dvw,384px)] [&>*]:flex [&>*]:flex-col"
      >
        <div className="size-full">
          <div className="flex items-center justify-between gap-5 px-4 pb-6">
            {sectionName === 'services' ? <div></div> : <NosIdWithText />}
            <SheetClosePrimitive aria-label={t('Common.Navbar.Close')} data-tid="button-close-menu">
              <Close width={32} height={32} className="text-nos-neutral-900" />
            </SheetClosePrimitive>
          </div>
          <NavSheetContent
            onClose={() => setSheetOpen(false)}
            goToSection={setSectionName}
            goToMainSection={() => setSectionName('main')}
            currentSection={sectionName}
          />
        </div>
      </SheetContentPrimitive>
    </SheetPrimitive>
  );
}

export function MobileNavbarTrigger() {
  const { isLoading } = useGetPersonalInfo();
  const { setSheetOpen } = useNavbarContext();
  return (
    <SkeletonLight
      loading={isLoading}
      className="bg-transparent text-nos-green/60 before:via-nos-lime"
      loaderChildren={<MenuRight width={32} height={32} />}
    >
      <button
        type="button"
        className="rounded-md outline-none focus-visible:outline-nos-blue"
        data-tid="button-open-menu"
        onClick={() => {
          setSheetOpen(true);
        }}
      >
        <MenuRight width={32} height={32} />
      </button>
    </SkeletonLight>
  );
}
